// src/lib/serviceWorkerClient.ts
// This module provides a client interface to interact with the service worker

import {
  storeFiles,
  createUploadTask,
  updateTaskStatus,
  getTask,
  getPendingTasks,
} from "./indexedDB-utils";
import type { SurfSpot, DiscountRule } from "@/types";

// Interface for the upload task
interface UploadTask {
  id: string;
  title: string;
  description?: string;
  price: string;
  selectedSpot: SurfSpot;
  fileKeys: string[];
  coverPhotoIndex: number;
  photoDate?: string;
  startTime?: string;
  endTime?: string;
  discountRules?: DiscountRule[];
  authToken: string;
  userId: string;
  status: "pending" | "uploading" | "completed" | "failed";
  progress: number;
  createdAt: string;
  updatedAt: string;
  albumId?: string;
  error?: string;
}

// Initialize the service worker client
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

// Register for service worker events
function registerForServiceWorkerEvents(): void {
  navigator.serviceWorker.addEventListener("message", (event) => {
    const message = event.data;

    switch (message.type) {
      case "UPLOAD_PROGRESS":
        // Handle upload progress updates
        console.log(
          "Upload progress:",
          message.taskId,
          message.overallProgress
        );
        updateTaskStatus(message.taskId, "uploading", message.overallProgress);
        break;

      case "UPLOAD_COMPLETED":
        // Handle upload completion
        console.log("Upload completed:", message.taskId, message.albumId);
        updateTaskStatus(message.taskId, "completed", 100);
        break;

      case "UPLOAD_FAILED":
        // Handle upload failure
        console.error("Upload failed:", message.taskId, message.error);
        updateTaskStatus(message.taskId, "failed").catch((err) =>
          console.error("Error updating task status:", err)
        );
        break;

      case "ALBUM_CREATED":
        // Handle album creation
        console.log("Album created:", message.albumId, message.title);
        break;

      case "TASK_RECEIVED":
        // Confirmation that the service worker received our task
        console.log("Task received by service worker:", message.taskId);
        break;

      case "TASK_ERROR":
        // Handle task error
        console.error("Task error:", message.taskId, message.error);
        break;
    }
  });
}

// Initialize the client
export async function initServiceWorkerClient(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service workers are not supported in this browser");
    return false;
  }

  try {
    // Get the service worker registration
    serviceWorkerRegistration = await navigator.serviceWorker.ready;

    // Register for service worker events
    registerForServiceWorkerEvents();

    return true;
  } catch (error) {
    console.error("Error initializing service worker client:", error);
    return false;
  }
}

// Start a background upload process
export async function startBackgroundUpload(
  files: File[],
  coverPhotoIndex: number,
  title: string,
  description: string,
  price: string,
  selectedSpot: SurfSpot,
  discountRules: DiscountRule[] = [],
  photoDate?: Date,
  startTime?: string,
  endTime?: string
): Promise<string | null> {
  try {
    // Check if service worker is ready
    if (!serviceWorkerRegistration) {
      const ready = await initServiceWorkerClient();
      if (!ready) {
        throw new Error("Service worker is not available");
      }
    }

    // Get auth token
    const { data: session } = await supabase.auth.getSession();
    const authToken = session?.session?.access_token;

    if (!authToken) {
      throw new Error("User is not authenticated");
    }

    // Store files in IndexedDB
    console.log("Storing files in IndexedDB...");
    const fileKeys = await storeFiles(files);

    // Create the upload task
    const taskId = await createUploadTask({
      title,
      description,
      price,
      selectedSpot,
      fileKeys,
      coverPhotoIndex,
      photoDate: photoDate?.toISOString(),
      startTime,
      endTime,
      discountRules: discountRules.filter((rule) => rule.enabled),
      authToken,
      userId: (await supabase.auth.getUser()).data.user?.id,
    });

    // Notify the service worker to start processing
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: "INIT_UPLOAD_TASK",
        task: await getTask(taskId),
      });

      console.log("Background upload task initialized:", taskId);
      return taskId;
    } else {
      throw new Error("Service worker controller not available");
    }
  } catch (error) {
    console.error("Error starting background upload:", error);
    return null;
  }
}

// Get all pending upload tasks
export async function getPendingUploadTasks(): Promise<UploadTask[]> {
  return getPendingTasks();
}

// Check the status of an upload task
export async function checkUploadStatus(
  taskId: string
): Promise<UploadTask | null> {
  try {
    // First check the local database
    const task = await getTask(taskId);

    // If the service worker is active, also ask it for the latest status
    if (navigator.serviceWorker.controller && task) {
      navigator.serviceWorker.controller.postMessage({
        type: "GET_TASK_STATUS",
        taskId,
      });
    }

    return task;
  } catch (error) {
    console.error("Error checking upload status:", error);
    return null;
  }
}
